import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import SortButton from 'assets/components/filters/sortButton/SortButton';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { cleanValues } from 'assets/utils/data/Object';
import { merge } from 'lodash';
import useClaimCreator from 'module/claims/hooks/useClaimCreator';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import ClaimCreateButtons from '../../claim/other/Claim.CreateButtons';
import { useContractProvider } from '../Contract.Hooks';
import ContractFilters from './Contract.Filters';
import ContractInfo from './Contract.Info';
import ContractStatus from 'app/components/ContractStatus';
import Box from 'assets/components/box/Box';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function ContractList() {
  const { lang } = getLocales();
  const { listApi, pageRouter } = useContractProvider();
  const claimCreatorHook = useClaimCreator();

  function handleSort(newFilters: Api.IDefaultListRequest) {
    pageRouter.updateQuery({
      ...pageRouter.query,
      list: cleanValues({
        ...pageRouter.query?.list,
        ...newFilters,
      }),
    });
  }
  return (
    <>
      <Page
        class={ThemePageList}
        isLoading={listApi.isExecuting}
        sidebar={<ContractInfo />}
        header={
          <>
            <h2 style={{ marginRight: 'auto' }} children={lang.contract} />
            {isMobile && <ContractFilters />}
          </>
        }
        subHeader={isDesktop && <ContractFilters />}
        render={
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th>
                  {lang.appNumber}
                  <SortButton
                    orderBy="appNumber"
                    thenBy={['client.FirstName', 'client.LastName', 'vehicle.Vin']}
                    apiBody={listApi.body}
                    onSort={handleSort}
                  />
                </th>
                <th>{lang.status}</th>
                <th>
                  {lang.product}
                  <SortButton orderBy="product.name" apiBody={listApi.body} onSort={handleSort} />
                </th>
                <th>
                  {lang.vehicle}/{lang.vin}
                  <SortButton
                    orderBy="vehicle.makeCode"
                    thenBy="vehicle.model"
                    apiBody={listApi.body}
                    onSort={handleSort}
                  />
                </th>
                <th>
                  {lang.client}
                  <SortButton
                    orderBy="client.firstName"
                    thenBy="client.lastName"
                    apiBody={listApi.body}
                    onSort={handleSort}
                  />
                </th>
                <th>
                  {lang.dealer}
                  <SortButton orderBy="dealer.name" apiBody={listApi.body} onSort={handleSort} />
                </th>
                <th>
                  {lang.createdBy}
                  <SortButton
                    orderBy="createdBy.firstName"
                    thenBy="createdBy.lastName"
                    apiBody={listApi.body}
                    onSort={handleSort}
                  />
                </th>
                <th>
                  {lang.createdAt}
                  <SortButton orderBy="createdAtUtc" apiBody={listApi.body} onSort={handleSort} />
                </th>
                <th>
                  {lang.expiryDate}
                  <SortButton orderBy="expiryDate" apiBody={listApi.body} onSort={handleSort} />
                </th>
                <th>{lang.noOfClaims}</th>
                <th data-actions>{lang.actions}</th>
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={listApi.payload?.data}
                render={({ data: contract }) => {
                  return (
                    <React.Fragment key={contract.id}>
                      <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                        <td>
                          <b>{contract.appNumberWithRevision}</b>
                        </td>
                        <td>
                          <ContractStatus statusCode={contract.statusCode} />
                        </td>
                        <td>{contract.product?.name || lang.unknownProduct}</td>
                        <td>
                          <span>
                            {contract.vehicle.makeCode} {contract.vehicle.model}
                          </span>
                          <span> {contract.vehicle.year?.toString()}</span>
                          <br />
                          <span>{contract.vehicle?.vin}</span>
                        </td>
                        <td>{contract.client?.name}</td>
                        <td>{contract.dealer?.name}</td>
                        <td>{contract.createdBy?.fullName || lang.unknownUser}</td>
                        <td>{localizeDate(contract?.createdAtLocal, defaultDateTimeFormat)}</td>
                        <td>{localizeDate(contract?.expiryDate, defaultDateFormat)}</td>
                        <td>{contract.claimCount ?? 'N / A'} </td>
                        <td>
                          <Box class={ThemeWrapperStack}>
                            <Button
                              class={ThemeButtonCircle}
                              media={<Icon class="fas-info-circle" />}
                              onClick={pageRouter.updateParams}
                              data={{ id: contract.id.toString() }}
                              htmlElementProps={{
                                title: lang.info,
                              }}
                              active
                            />
                            <ClaimCreateButtons contract={contract} claimCreatorHook={claimCreatorHook} />
                            <LinkButton
                              class={ThemeButtonCircle}
                              url={contract.documentationAggregate?.url}
                              media={<Icon class="fas-download" />}
                              target="_blank"
                              disabled={!contract?.documentationAggregate?.url}
                              htmlElementProps={{
                                title: !contract?.documentationAggregate?.url
                                  ? lang.missingContractDocument
                                  : lang.download,
                              }}
                              active
                            />
                          </Box>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                }}
              />
            </tbody>
          </table>
        }
        footer={
          <Pagination
            class={ThemePaginationRounded}
            count={listApi.payload?.count}
            offset={listApi.body?.offset}
            limit={listApi.body?.limit}
            onLimitChange={(limit) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    limit,
                    offset: 0,
                  },
                })
              )
            }
            onOffsetChange={(offset) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    offset,
                  },
                })
              )
            }
          />
        }
      />
    </>
  );
}
